import React, { useState } from 'react';
import { PageProps } from '@wix/design-system';
import { EntityPageState } from '../../state';
import { EntityPageHeader } from '../EntityPageHeader';
import { EntityPageContent } from '../EntityPageContent';
import { EntityPageCard } from '../EntityPageCard';
import { EntityPageExtensions } from '../EntityPageExtensions';
import { EntityPageMainContent } from '../EntityPageMainContent';
import { EntityPageAdditionalContent } from '../EntityPageAdditionalContent';
import { observer } from 'mobx-react-lite';
import { EntityPageWrapper } from './EntityPageWrapper';
import { EntityPageContextProvider } from '../../providers';
import { FieldValues, FormProvider } from 'react-hook-form';
import { minimalRequiredWdsRuntimeCheck } from '../../minimalRequiredWdsRuntimeCheck';
import { FormPageProvider } from '../FormPage';

export interface EntityPageProps<T, V extends FieldValues = FieldValues>
  extends PageProps {
  /**
   * EntityPageState instance created with the `useEntityPage` hook
   */
  state: EntityPageState<T, V>;
}

function _EntityPage<T, V extends FieldValues = FieldValues>({
  state,
  ...rest
}: EntityPageProps<T, V>) {
  if (process.env.NODE_ENV !== 'production') {
    minimalRequiredWdsRuntimeCheck({
      required: '^1.73.0',
      requiredBy: 'Cairo/EntityPage',
    });
  }

  useState(() => {
    state.init();
  });

  return (
    <EntityPageContextProvider value={state}>
      <FormPageProvider state={state.formPage}>
        <FormProvider {...state.form}>
          <EntityPageWrapper state={state} {...rest} />
        </FormProvider>
      </FormPageProvider>
    </EntityPageContextProvider>
  );
}

_EntityPage.Header = EntityPageHeader;
_EntityPage.Content = EntityPageContent;
_EntityPage.MainContent = EntityPageMainContent;
_EntityPage.AdditionalContent = EntityPageAdditionalContent;
_EntityPage.Card = EntityPageCard;
_EntityPage.Extensions = EntityPageExtensions;

_EntityPage.displayName = 'EntityPage';

export const EntityPage = observer(_EntityPage);
